"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.doesShapeAcceptOperation = doesShapeAcceptOperation;
exports.passFullOperationToSource = passFullOperationToSource;
const context_entries_1 = require("@comunica/context-entries");
const utils_algebra_1 = require("@comunica/utils-algebra");
const Utils_1 = require("./Utils");
/**
 * Check if the given shape accepts the given query operation.
 * @param shape A shape to test the query operation against.
 * @param operation A query operation to test.
 * @param options Additional options to consider.
 * @param options.joinBindings If additional bindings will be pushed down to the source for joining.
 * @param options.filterBindings If additional bindings will be pushed down to the source for filtering.
 */
function doesShapeAcceptOperation(shape, operation, options) {
    // Cache for doesShapeAcceptOperationRecurseOperation results, keyed by operation object identity.
    // This avoids exponential blowup when a disjunction shape has multiple children: each failing
    // shape child would otherwise redundantly re-traverse the entire operation subtree.
    const operationRecurseCache = new Map();
    return doesShapeAcceptOperationRecurseShape(shape, shape, operation, operationRecurseCache, options);
}
function doesShapeAcceptOperationRecurseShape(shapeTop, shapeActive, operation, operationRecurseCache, options) {
    // Recurse into the shape
    if (shapeActive.type === 'conjunction') {
        return shapeActive.children
            .every(child => doesShapeAcceptOperationRecurseShape(shapeTop, child, operation, operationRecurseCache, options));
    }
    if (shapeActive.type === 'disjunction') {
        return shapeActive.children
            .some(child => doesShapeAcceptOperationRecurseShape(shapeTop, child, operation, operationRecurseCache, options));
    }
    if (shapeActive.type === 'negation') {
        // The negation changes shapeTop, so we must use a fresh cache to avoid mixing results
        // from different shapeTop scopes.
        const negationCache = new Map();
        return !doesShapeAcceptOperationRecurseShape(shapeActive.child, shapeActive.child, operation, negationCache, options);
    }
    if (shapeActive.type === 'arity') {
        return doesShapeAcceptOperationRecurseShape(shapeTop, shapeActive.child, operation, operationRecurseCache, options);
    }
    // Validate options
    if ((options?.joinBindings && !shapeActive.joinBindings) ??
        (options?.filterBindings && !shapeActive.filterBindings)) {
        return false;
    }
    // Check if the shape's operation matches with the given operation
    const shapeOperation = shapeActive.operation;
    switch (shapeOperation.operationType) {
        case 'type': {
            if (shapeOperation.type === utils_algebra_1.Algebra.Types.EXPRESSION && isExtensionFunction(operation) &&
                !('extensionFunctions' in shapeOperation &&
                    shapeOperation.extensionFunctions?.includes(operation.name.value))) {
                return false;
            }
            if (!doesShapeAcceptOperationRecurseOperationAndShape(shapeTop, shapeActive.children, operation, operationRecurseCache, options) &&
                !doesShapeAcceptOperationRecurseOperation(shapeTop, operation, operationRecurseCache, options)) {
                return false;
            }
            return shapeOperation.type === operation.type;
        }
        case 'pattern': {
            if (!doesShapeAcceptOperationRecurseOperationAndShape(shapeTop, shapeActive.children, operation, operationRecurseCache, options) &&
                !doesShapeAcceptOperationRecurseOperation(shapeTop, operation, operationRecurseCache, options)) {
                return false;
            }
            return shapeOperation.pattern.type === operation.type;
        }
        case 'wildcard': {
            // All possible operations are accepted by this shape.
            // As exception, extension functions are not accepted through wildcards.
            if (options?.wildcardAcceptAllExtensionFunctions) {
                return true;
            }
            if (isExtensionFunction(operation)) {
                return false;
            }
            // Also check for nested extension functions,
            // and only accept the wildcard if all nested extension functions are supported by the query shape.
            let hasUnsupportedExtensionFunction = false;
            utils_algebra_1.algebraUtils.visitOperation(operation, {
                [utils_algebra_1.Algebra.Types.EXPRESSION]: {
                    visitor: (subOp) => {
                        if (isExtensionFunction(subOp) && !doesShapeAcceptOperation(shapeTop, subOp, options)) {
                            hasUnsupportedExtensionFunction = true;
                            return false;
                        }
                        return true;
                    },
                },
            });
            return !hasUnsupportedExtensionFunction;
        }
    }
}
function doesShapeAcceptOperationRecurseOperationAndShape(shapeTop, shapeActiveChildren, operation, operationRecurseCache, options) {
    if (isExtensionFunction(operation) || isExtensionFunction(operation.expression)) {
        return false;
    }
    if (shapeActiveChildren) {
        const operationCast = operation;
        const operationInputs = operationCast.input ?
            (Array.isArray(operationCast.input) ? operationCast.input : [operationCast.input]) :
            operationCast.patterns ?? [];
        for (const [i, shapeActiveChild] of shapeActiveChildren.entries()) {
            if (!operationInputs[i] ||
                !doesShapeAcceptOperationRecurseShape(shapeTop, shapeActiveChild, operationInputs[i], operationRecurseCache, options)) {
                return false;
            }
        }
        return true;
    }
    return false;
}
function doesShapeAcceptOperationRecurseOperation(shapeTop, operation, cache, options) {
    // Check the cache first to avoid redundant subtree traversals.
    // When a shape has multiple disjunction/conjunction children, each failing child would otherwise
    // re-traverse the entire operation subtree independently, leading to exponential blowup.
    const cached = cache.get(operation);
    if (cached !== undefined) {
        return cached;
    }
    const result = doesShapeAcceptOperationRecurseOperationImpl(shapeTop, operation, cache, options);
    cache.set(operation, result);
    return result;
}
function doesShapeAcceptOperationRecurseOperationImpl(shapeTop, operation, cache, options) {
    // Recurse into the operation, and restart from the top-level shape
    const operationCast = operation;
    if (operationCast.input) {
        const inputs = Array
            .isArray(operationCast.input) ?
            operationCast.input :
            [operationCast.input];
        if (!inputs.every(input => doesShapeAcceptOperationRecurseShape(shapeTop, shapeTop, input, cache, options))) {
            return false;
        }
    }
    if (operationCast.expression && isExtensionFunction(operationCast.expression) &&
        !doesShapeAcceptOperationRecurseShape(shapeTop, shapeTop, operationCast.expression, cache, options)) {
        return false;
    }
    const patternAccepted = (input) => doesShapeAcceptOperationRecurseShape(shapeTop, shapeTop, input, cache, options);
    return !(operationCast.patterns && !operationCast.patterns.every(patternAccepted));
}
function isStandardSparqlFunction(iri) {
    return /^https?:\/\/www\.w3\.org\//u.test(iri);
}
function isExtensionFunction(operation) {
    return operation && operation.type === utils_algebra_1.Algebra.Types.EXPRESSION &&
        (0, utils_algebra_1.isKnownSubType)(operation, utils_algebra_1.Algebra.ExpressionTypes.NAMED) && !isStandardSparqlFunction(operation.name.value);
}
async function passFullOperationToSource(operation, sources, context) {
    if (sources.length === 1) {
        const sourceWrapper = sources[0];
        const destination = context.get(context_entries_1.KeysRdfUpdateQuads.destination);
        if (!destination || sourceWrapper.source.referenceValue === (0, Utils_1.getDataDestinationValue)(destination)) {
            try {
                const shape = await sourceWrapper.source.getSelectorShape(context);
                if (doesShapeAcceptOperation(shape, operation)) {
                    return true;
                }
            }
            catch {
                // Fallback to the default case when the selector shape does not exist,
                // which can occur for a non-existent destination.
            }
        }
    }
    return false;
}
//# sourceMappingURL=FragmentSelectorShapes.js.map